import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation, SUPPORTED_LANGUAGES } from "@/hooks/useTranslation";
import api from "@/lib/api";
import {
  LayoutDashboard, Truck, HardHat, Eye, ChevronRight,
  Globe, Loader2, Zap, ArrowLeft, Bird
} from "lucide-react";

const ROLES = [
  { id: "planner", icon: LayoutDashboard, color: "bg-lime-500 text-slate-950", label: "planner", descKey: "role_desc_planner" },
  { id: "machinist", icon: HardHat, color: "bg-amber-500 text-slate-950", label: "machinist", descKey: "role_desc_machinist" },
  { id: "driver", icon: Truck, color: "bg-red-600 text-white", label: "driver", descKey: "role_desc_driver" },
  { id: "viewer", icon: Eye, color: "bg-slate-600 text-white", label: "viewer", descKey: "role_desc_viewer" },
];

export default function Lobby() {
  const { session, setSession } = useAppContext();
  const nav = useNavigate();
  const [step, setStep] = useState("hub");
  const [hubs, setHubs] = useState([]);
  const [selectedHub, setSelectedHub] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedLang, setSelectedLang] = useState(() => localStorage.getItem("pigeon-lang") || "en");
  const [selectedDriver, setSelectedDriver] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [hubLoading, setHubLoading] = useState(true);
  const [userName, setUserName] = useState("");
  const { t } = useTranslation(selectedLang);

  useEffect(() => {
    if (session?.hubId && session?.role) {
      navigateToRole(session.role, session.driverName);
    }
    loadHubs();
  }, []);

  const loadHubs = async () => {
    setHubLoading(true);
    try {
      let h = await api.getHubs();
      if (h.length === 0) {
        await api.seed();
        h = await api.getHubs();
      }
      setHubs(h);
    } catch (e) { void 0; }
    setHubLoading(false);
  };

  const navigateToRole = (role, driverName) => {
    if (role === "planner") nav("/planner");
    else if (role === "driver") nav(driverName ? `/driver/${driverName}` : "/driver");
    else if (role === "machinist") nav("/machinist");
    else if (role === "viewer") nav("/viewer");
  };

  const handleHubSelect = async (hub) => {
    setSelectedHub(hub);
    try {
      const d = await api.getDrivers(hub.id);
      setDrivers(d);
    } catch (e) { void 0; }
    setStep("role");
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    if (role === "driver" && drivers.length > 0) {
      setStep("driver-select");
    } else {
      setStep("language");
    }
  };

  const handleDriverSelect = (driverName) => {
    setSelectedDriver(driverName);
    setUserName(driverName);
    setStep("language");
  };

  const handleEnter = () => {
    const sess = {
      hubId: selectedHub.id,
      hubName: selectedHub.name,
      role: selectedRole,
      lang: selectedLang,
      driverName: selectedDriver || userName || (selectedRole === "planner" ? "Planner" : selectedRole),
      userName: userName || selectedDriver || selectedRole,
      asset_type: selectedHub.asset_type || "refueling",
      asset_config: selectedHub.asset_config || {},
    };
    setSession(sess);
    navigateToRole(selectedRole, selectedDriver);
  };

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col relative overflow-hidden" data-testid="lobby-screen">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1641023388239-39dd1171a64f?w=1600&q=60"
          alt="Industrial background"
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/95 to-white" />
      </div>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 py-5 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center shadow-md">
            <Bird className="w-5 h-5 text-slate-950" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">Pigeon Supply</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{t("lobby_subtitle")}</p>
          </div>
        </div>
        {step !== "hub" && (
          <button
            data-testid="lobby-back-btn"
            onClick={() => setStep(step === "language" && selectedRole === "driver" ? "driver-select" : step === "language" ? "role" : step === "driver-select" ? "role" : "hub")}
            className="btn-industrial px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-xs border border-slate-200"
          >
            <ArrowLeft className="w-4 h-4" /> {t("back")}
          </button>
        )}
      </header>

      {/* Content */}
      <main className="relative z-10 flex-1 flex items-center justify-center p-6">
        {/* HUB SELECT */}
        {step === "hub" && (
          <div className="w-full max-w-lg animate-fadeInUp">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase text-slate-900 mb-2">{t("select_hub")}</h2>
              <p className="text-sm text-slate-400 font-medium">Choose your logistics command hub</p>
            </div>
            {hubLoading ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-lime-500 animate-spin" /></div>
            ) : (
              <div className="grid gap-4">
                {hubs.map((hub, i) => (
                  <button
                    key={hub.id}
                    data-testid={`hub-tile-${hub.id}`}
                    onClick={() => handleHubSelect(hub)}
                    className={`group w-full p-6 bg-slate-50 border border-slate-200 rounded-xl hover:border-lime-400 transition-all text-left flex items-center justify-between active:scale-[0.98] animate-fadeInUp stagger-${i + 1}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center group-hover:bg-lime-50 transition-colors border border-slate-200 group-hover:border-lime-300">
                        <Zap className="w-6 h-6 text-lime-500" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-900 tracking-tight">{hub.name}</h3>
                        <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mt-1">{hub.hub_type} {hub.description && `/ ${hub.description}`}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-lime-500 group-hover:translate-x-1 transition-all" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ROLE SELECT */}
        {step === "role" && (
          <div className="w-full max-w-xl animate-fadeInUp">
            <div className="text-center mb-10">
              <p className="text-xs font-bold tracking-widest uppercase text-lime-500 mb-2">{selectedHub?.name}</p>
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase text-slate-900 mb-2">{t("select_role")}</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {ROLES.map((role, i) => {
                const Icon = role.icon;
                return (
                  <button
                    key={role.id}
                    data-testid={`role-tile-${role.id}`}
                    onClick={() => handleRoleSelect(role.id)}
                    className={`group relative overflow-hidden p-6 bg-slate-50 border border-slate-200 rounded-xl hover:border-lime-400 transition-all text-left active:scale-[0.97] animate-fadeInUp stagger-${i + 1}`}
                  >
                    <div className={`w-12 h-12 ${role.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                      <Icon className="w-6 h-6" strokeWidth={2} />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 uppercase tracking-tight">{t(role.label)}</h3>
                    <p className="text-[11px] text-slate-400 mt-1 font-medium leading-relaxed">{t(role.descKey)}</p>
                    <div className="absolute top-0 right-0 w-24 h-24 bg-slate-100/30 rounded-full -translate-y-8 translate-x-8 group-hover:bg-lime-500/5 transition-colors" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* DRIVER SELECT */}
        {step === "driver-select" && (
          <div className="w-full max-w-md animate-fadeInUp">
            <div className="text-center mb-10">
              <p className="text-xs font-bold tracking-widest uppercase text-lime-500 mb-2">{t("driver")}</p>
              <h2 className="text-3xl font-black tracking-tighter uppercase text-slate-900 mb-2">{t("switch_driver")}</h2>
            </div>
            <div className="grid gap-3">
              {drivers.map((d, i) => (
                <button
                  key={d.driver_name}
                  data-testid={`driver-select-${d.driver_name}`}
                  onClick={() => handleDriverSelect(d.driver_name)}
                  className={`group w-full p-5 bg-slate-50 border border-slate-200 rounded-xl hover:border-lime-400 transition-all text-left flex items-center justify-between active:scale-[0.98] animate-fadeInUp stagger-${i + 1}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center text-white font-black text-lg">
                      {d.driver_name[0]}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900 uppercase tracking-tight">{d.driver_name}</h3>
                      <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">{d.region}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`status-dot ${d.status === "ACTIVE" ? "online" : "offline"}`} />
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-lime-500 transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* LANGUAGE GATE (Zero Gate) */}
        {step === "language" && (
          <div className="w-full max-w-lg animate-fadeInUp">
            <div className="text-center mb-8">
              <Globe className="w-10 h-10 text-lime-500 mx-auto mb-4" />
              <h2 className="text-3xl font-black tracking-tighter uppercase text-slate-900 mb-2">{t("language_gate")}</h2>
              <p className="text-sm text-slate-400 font-medium">Zero Gate — All UI text will be translated</p>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-8 max-h-[40vh] overflow-y-auto pr-1">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  data-testid={`lang-${lang.code}`}
                  onClick={() => setSelectedLang(lang.code)}
                  className={`p-3 rounded-xl text-center transition-all border font-bold text-sm tracking-tight ${
                    selectedLang === lang.code
                      ? "bg-lime-50 border-lime-500 text-lime-600 shadow-sm"
                      : "bg-slate-50 border-slate-200 text-slate-400 hover:border-slate-300"
                  }`}
                >
                  <span className="block text-lg font-black mb-1">{lang.flag}</span>
                  <span className="block text-[10px] uppercase tracking-wider">{lang.label}</span>
                </button>
              ))}
            </div>

            {selectedRole !== "planner" && selectedRole !== "viewer" && !selectedDriver && (
              <div className="mb-6">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Your Name</label>
                <input
                  data-testid="user-name-input"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Enter your name..."
                  className="w-full p-4 bg-white border border-slate-200 rounded-xl text-slate-900 font-bold placeholder:text-slate-400 focus:border-lime-500 focus:ring-1 focus:ring-lime-500/20 outline-none transition-all"
                />
              </div>
            )}

            <button
              data-testid="enter-dashboard-btn"
              onClick={handleEnter}
              className="btn-industrial w-full py-5 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-sm font-black shadow-md hover:shadow-lg"
            >
              {t("enter_dashboard")}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="relative z-10 text-center py-4 border-t border-slate-100">
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">Pigeon Supply v1.0 — Supply Platform</p>
      </footer>
    </div>
  );
}
